import { useEffect, useRef } from "react";

interface ConfettiProps {
  active: boolean;
  /** 紙吹雪の数 */
  count?: number;
  /** 自動で消えるまでのms */
  duration?: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  rotation: number;
  rotationSpeed: number;
  shape: "rect" | "circle" | "star";
  alpha: number;
  decay: number;
}

const COLORS = [
  "#fbbf24", "#f59e0b", "#f97316", "#22c55e",
  "#10b981", "#ffffff", "#fde68a", "#fed7aa",
];

function createParticle(canvasW: number, canvasH: number): Particle {
  const angle = (Math.random() * Math.PI * 2);
  const speed = 4 + Math.random() * 8;
  return {
    x: canvasW / 2 + (Math.random() - 0.5) * canvasW * 0.6,
    y: canvasH * 0.4 + Math.random() * canvasH * 0.2,
    vx: Math.cos(angle) * speed * 0.6,
    vy: -(3 + Math.random() * 6),
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    size: 5 + Math.random() * 8,
    rotation: Math.random() * Math.PI * 2,
    rotationSpeed: (Math.random() - 0.5) * 0.3,
    shape: (["rect", "circle", "star"] as const)[Math.floor(Math.random() * 3)],
    alpha: 1,
    decay: 0.012 + Math.random() * 0.008,
  };
}

function drawStar(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.beginPath();
  for (let i = 0; i < 5; i++) {
    const a = (i * 4 * Math.PI) / 5 - Math.PI / 2;
    const b = (i * 4 * Math.PI) / 5 + (2 * Math.PI) / 5 - Math.PI / 2;
    if (i === 0) ctx.moveTo(x + r * Math.cos(a), y + r * Math.sin(a));
    else ctx.lineTo(x + r * Math.cos(a), y + r * Math.sin(a));
    ctx.lineTo(x + (r * 0.4) * Math.cos(b), y + (r * 0.4) * Math.sin(b));
  }
  ctx.closePath();
}

export default function Confetti({ active, count = 120, duration = 3000 }: ConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const particlesRef = useRef<Particle[]>([]);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // パーティクル生成
    particlesRef.current = Array.from({ length: count }, () =>
      createParticle(canvas.width, canvas.height)
    );

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current = particlesRef.current.filter((p) => p.alpha > 0.01);

      for (const p of particlesRef.current) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.18; // 重力
        p.vx *= 0.99;
        p.rotation += p.rotationSpeed;
        p.alpha -= p.decay;

        ctx.save();
        ctx.globalAlpha = Math.max(0, p.alpha);
        ctx.fillStyle = p.color;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);

        if (p.shape === "rect") {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else if (p.shape === "circle") {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          drawStar(ctx, 0, 0, p.size / 2);
          ctx.fill();
        }
        ctx.restore();
      }

      if (particlesRef.current.length > 0) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    animRef.current = requestAnimationFrame(animate);

    // duration後に強制終了
    const timer = setTimeout(() => {
      cancelAnimationFrame(animRef.current);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current = [];
    }, duration);

    return () => {
      cancelAnimationFrame(animRef.current);
      clearTimeout(timer);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
  }, [active, count, duration]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[99998]"
      style={{ width: "100vw", height: "100vh" }}
    />
  );
}
